import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../Leads.css";
import { Search, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmModal from "../../../components/ConfirmModal";
import { NOT_FOUND_B_2 as COLUMN_MAP } from "../../../utils/column_maps";
import { getAlignClass, mapLeadDataNotFound } from "../../../utils/common";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";

const NotFoundB2 = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const leadsPerPage = 25;
  const currentPageFromParams = Number(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(currentPageFromParams);

  // const [deleteMultiple, setDeleteMultiple] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllPage, setSelectAllPage] = useState(false);
  // const [selectAllGlobal, setSelectAllGlobal] = useState(false);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const totalPages = Math.ceil(filteredData.length / leadsPerPage);

  const fetchData = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/leads-b?found=false&lead_status=1`);
      if (!res.status === 200) {
        throw new Error("❌ Failed to fetch leads");
      }
      const leads = await res.data;
      const cleaned = leads.data.map((row) => {
        const obj = { ...row };
        COLUMN_MAP.forEach(({ key }) => {
          if (!(key in obj)) {
            obj[key] = "";
          }
        });

        return obj;
      });

      setAllData(cleaned);
      setFilteredData(cleaned);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (identifier) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/lead-b/${identifier}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ lead_status: 1 }),
      });

      if (!res.ok) throw new Error("Failed to Delete lead");

      toast.success("Lead Deleted successfully.");

      const updatedData = allData.filter((item) => item.identifier !== identifier);
      setAllData(updatedData);
      setFilteredData(updatedData);
    } catch (err) {
      console.error("Error updating lead status:", err);
      toast.error(err.message || "Error Deleting Lead.");
    }
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", page);
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  useEffect(() => {
    fetchData(true);
    if (currentPage === 1) {
      const interval = setInterval(() => {
        fetchData(false);
      }, 60000);

      return () => clearInterval(interval);
    } // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (!search) {
      setFilteredData(allData);
      // setCurrentPage(1);
      // const newParams = new URLSearchParams(searchParams.toString());
      // newParams.set("page", 1);
      // setSearchParams(newParams);
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = allData.filter((row) => Object.values(row).some((value) => value && value.toString().toLowerCase().includes(searchLower)));

    setFilteredData(filtered);
    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", 1);
    setSearchParams(newParams); // eslint-disable-next-line
  }, [search, allData]);

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 3;

    if (totalPages > 0) {
      buttons.push(
        <button key={1} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(1)}>
          1
        </button>,
      );
    }

    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(2, endPage - maxVisiblePages + 1);
    }

    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-start" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === i ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(i)}>
          {i}
        </button>,
      );
    }

    if (endPage < totalPages - 1 && totalPages > 1) {
      buttons.push(
        <span key="ellipsis-end" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    if (totalPages > 1) {
      buttons.push(
        <button key={totalPages} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(totalPages)}>
          {totalPages}
        </button>,
      );
    }

    return buttons;
  };

  const totalTableWidth = COLUMN_MAP.reduce((sum, col) => sum + col.width, 0) + 80;

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleSelectAllPage = (checked) => {
    setSelectAllPage(checked);
    // setSelectAllGlobal(false);
    const currentPageIds = filteredData.slice(indexOfFirstLead, indexOfLastLead).map((lead) => lead.id);
    if (checked) {
      setSelectedIds([...new Set([...selectedIds, ...currentPageIds])]);
    } else {
      setSelectedIds(selectedIds.filter((id) => !currentPageIds.includes(id)));
    }
  };

  // const handleSelectAllGlobal = (checked) => {
  //   setSelectAllGlobal(checked);
  //   setSelectAllPage(false);
  //   if (checked) {
  //     const allIds = filteredData.map((lead) => lead.identifier);
  //     setSelectedIds(allIds);
  //   } else {
  //     setSelectedIds([]);
  //   }
  // };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((id) =>
          fetch(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/lead-b/${id}`, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ lead_status: 1 }),
          }),
        ),
      );

      toast.success(`${selectedIds.length} lead${selectedIds.length > 1 ? "s" : ""} deleted successfully.`);

      const updatedData = allData.filter((item) => !selectedIds.includes(item.id));
      setAllData(updatedData);
      setFilteredData(updatedData);
      setSelectedIds([]);
      setSelectAllPage(false);
    } catch (err) {
      console.error("Error deleting multiple leads:", err);
      toast.error("Error deleting selected leads.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="input_fields_container">
        <div className="">
          <input type="text" placeholder="Search leads..." value={search} onChange={(e) => setSearch(e.target.value)} className="!mb-4 !p-2 !border !border-gray-300 !rounded-lg !w-full focus:!outline-none focus:!ring-2 focus:!ring-blue-500" />
        </div>
      </div>

      <div className="">
        <div className="!overflow-auto !w-full">
          <table className="!border !border-gray-300 !bg-white !rounded-lg !shadow !table-auto" style={{ width: `${totalTableWidth}px` }}>
            <thead className="!bg-gray-100 sticky top-0 z-[1]">
              {selectedIds?.length > 0 && (
                <tr>
                  <th colSpan={COLUMN_MAP.length + 4} className="!px-4 !py-2 !border !border-gray-300 bg-sky-50">
                    <div className="flex items-center !space-x-6">
                      <span className="text-gray-700 text-sm">
                        You have selected <span className="font-semibold">{selectedIds.length}</span> record{selectedIds.length > 1 ? "s" : ""}.
                      </span>

                      <button onClick={() => setBulkDeleteModalOpen(true)} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white !px-4 !py-1.5 rounded-md text-sm shadow-sm transition cursor-pointer">
                        <Trash2 size={16} />
                        <span>Delete ({selectedIds.length})</span>
                      </button>
                    </div>
                  </th>
                </tr>
              )}
              <tr>
                <th className="!border-none !font-semibold text-nowrap !w-10 !min-w-8 flex justify-center item-center">
                  <input type="checkbox" checked={selectAllPage} onChange={(e) => handleSelectAllPage(e.target.checked)} title="Select all on this page" className="cursor-pointer outline-none !w-4 !h-4 !m-0 !mt-2" />
                </th>
                {/* <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8">
                  <input type="checkbox" checked={selectAllGlobal} onChange={(e) => handleSelectAllGlobal(e.target.checked)} title="Select all on all pages" />
                </th> */}
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {COLUMN_MAP.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap ${getAlignClass(col.headerAlign)}`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading ? (
                <>
                  {filteredData.length > 0 ? (
                    filteredData.slice(indexOfFirstLead, indexOfLastLead).map((lead, idx) => {
                      const row = mapLeadDataNotFound(lead);
                      const isChecked = selectedIds.includes(row.id);
                      return (
                        <tr key={idx} className="hover:!bg-gray-50 even:!bg-gray-50">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <input type="checkbox" checked={isChecked} onChange={() => handleSelectRow(row.id)} className="cursor-pointer outline-none !w-4 !h-4 !m-0 !mt-2" />
                          </td>
                          {/* <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4"></td> */}
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={`/property-search?city=${encodeURIComponent(row["city"] || "")}
&state=${encodeURIComponent(row["state"] || "")}
&county=${encodeURIComponent(row["county"] || "")}
&beds=${encodeURIComponent(row["beds"] ?? "")}
&baths=${encodeURIComponent(row["baths"] ?? "")}
&building_area=${encodeURIComponent(row["building_sqft"] ?? "")}
&lot_area=${encodeURIComponent(row["lot_land_sqft"] ?? "")}
&lead-b=${encodeURIComponent(row["id"])}`}
                              className="!text-blue-600 hover:!text-blue-800 flex items-center justify-center"
                              title="Search Property"
                            >
                              <Search size={18} />
                            </a>
                          </td>

                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button onClick={() => handleOpenDeleteModal(row)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Search Property">
                              <Trash2 size={18} />
                            </button>
                          </td>

                          {COLUMN_MAP.map((col) => (
                            <td
                              key={col.key}
                              className={`!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap ${getAlignClass(col.cellAlign)}`}
                              style={{
                                maxWidth: `${col.width}px`,
                                width: `${col.width}px`,
                              }}
                              title={row[col.key]}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={COLUMN_MAP.length + 3} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr className="hover:!bg-gray-50 even:!bg-gray-50">
                    <td colSpan={COLUMN_MAP.length + 3} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        {filteredData.length > 0 && (
          <div className="flex flex-col items-center !mb-10 mt-4">
            <span className="text-sm text-gray-700">
              Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, filteredData.length)}</span> of <span className="font-semibold text-gray-900">{filteredData.length}</span> Entries
            </span>

            <div className="inline-flex !mt-2 xs:mt-0">
              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>
                Prev
              </button>

              {getPaginationButtons()}

              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages}>
                Next
              </button>
            </div>
          </div>
        )}
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete this lead?`}
          isOpen={deleteModalOpen}
          onConfirmTitle="Delete"
          onConfirm={() => {
            if (selectedRow) {
              handleDeleteLead(selectedRow.id);
            }
            setDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setDeleteModalOpen(false)}
        />
        <ConfirmModal
          isOpen={bulkDeleteModalOpen}
          title="Confirm Bulk Delete"
          message={`Are you sure you want to delete ${selectedIds.length} selected lead${selectedIds.length > 1 ? "s" : ""}?`}
          onConfirmTitle="Delete Selected"
          onConfirm={() => {
            handleBulkDelete();
            setBulkDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setBulkDeleteModalOpen(false)}
        />
      </div>
    </>
  );
};

export default NotFoundB2;
